import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { findContentById, getAllItems } from '../data/course';

const fadeIn = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] } },
};

export default function ContentPage() {
  const { contentId } = useParams();
  const found = findContentById(contentId);

  const allItems = getAllItems();
  const currentIndex = allItems.findIndex((item) => item.id === contentId);
  const prevItem = currentIndex > 0 ? allItems[currentIndex - 1] : null;
  const nextItem = currentIndex < allItems.length - 1 ? allItems[currentIndex + 1] : null;

  if (!found) {
    return (
      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10">
        <p className="text-gray-500">Content not found.</p>
        <Link to="/" className="text-sm text-gray-900 underline mt-4 inline-block">
          Back to overview
        </Link>
      </div>
    );
  }

  const { type, content, week } = found;

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-10 pb-24">
      <motion.article
        initial="hidden"
        animate="visible"
        variants={{
          visible: { transition: { staggerChildren: 0.06 } },
        }}
        className="max-w-3xl"
      >
        {/* Week badge */}
        <motion.div variants={fadeIn} className="flex items-center gap-x-2 text-sm/6 mb-6">
          <Link
            to="/"
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            Week {week.number}
          </Link>
          <span className="text-gray-950/25">/</span>
          <span className="text-gray-500">
            {type === 'live' ? 'Live Session' : type === 'article' ? 'Article' : 'Worksheet'}
          </span>
        </motion.div>

        {/* Title */}
        <motion.h1
          variants={fadeIn}
          className="text-2xl sm:text-3xl font-semibold text-gray-900 tracking-tight leading-tight"
        >
          {content.title}
        </motion.h1>

        {/* Meta */}
        <motion.div variants={fadeIn} className="flex items-center gap-x-4 text-sm text-gray-400 mt-3 mb-10">
          {type === 'live' && (
            <>
              <span>{content.duration}</span>
              <span className="text-gray-950/15">&middot;</span>
              <span>Date: {content.date}</span>
              <span className="text-gray-950/15">&middot;</span>
              <span>{content.time}</span>
            </>
          )}
          {type === 'article' && (
            <>
              <span>{content.type}</span>
              <span className="text-gray-950/15">&middot;</span>
              <span>{content.readingTime} read</span>
            </>
          )}
          {type === 'worksheet' && (
            <>
              <span>{content.type}</span>
              <span className="text-gray-950/15">&middot;</span>
              <span>{content.completionTime} to complete</span>
            </>
          )}
        </motion.div>

        {/* Live Session video placeholder */}
        {type === 'live' && (
          <motion.div
            variants={fadeIn}
            className="bg-[#0a0a0b] rounded-xl aspect-[16/9] mb-12 flex items-center justify-center overflow-hidden"
          >
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-white/[0.08] flex items-center justify-center mx-auto mb-3 hover:bg-white/[0.12] transition-colors cursor-pointer">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-white/40 ml-0.5">
                  <path d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653Z" />
                </svg>
              </div>
              <p className="text-sm text-white/25">Live session — Date TBD</p>
              <p className="text-xs text-white/15 mt-1">Recording available within 24 hours</p>
            </div>
          </motion.div>
        )}

        {/* Description */}
        <motion.div variants={fadeIn} className="mb-10">
          <p className="text-base/7 text-gray-600">
            {content.description}
          </p>
        </motion.div>

        {/* Topics */}
        {content.topics && content.topics.length > 0 && (
          <motion.div variants={fadeIn} className="mb-10">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">
              {type === 'live' ? "What we'll cover" : 'What this covers'}
            </h2>
            <ul className="space-y-3">
              {content.topics.map((topic, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -6 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.04, duration: 0.3 }}
                  className="flex items-start gap-3"
                >
                  <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-gray-300" />
                  <span className="text-[15px] text-gray-600 leading-relaxed">{topic}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}

        {/* Interactive segment */}
        {type === 'live' && content.interactive && (
          <motion.div
            variants={fadeIn}
            className="mb-10 rounded-xl border border-gray-950/[0.06] bg-gray-950/[0.02] p-6"
          >
            <h3 className="text-sm font-semibold text-gray-900 mb-2">
              Interactive segment
            </h3>
            <p className="text-[15px] text-gray-600 leading-relaxed">
              {content.interactive}
            </p>
          </motion.div>
        )}

        {/* Takeaway */}
        {type === 'live' && content.takeaway && (
          <motion.div
            variants={fadeIn}
            className="mb-10 rounded-xl border border-gray-950/[0.06] bg-gray-950/[0.02] p-6"
          >
            <h3 className="text-sm font-semibold text-gray-900 mb-2">
              You'll leave with
            </h3>
            <p className="text-[15px] text-gray-600 leading-relaxed">
              {content.takeaway}
            </p>
          </motion.div>
        )}

        {/* Extras */}
        {content.extras && (
          <motion.div
            variants={fadeIn}
            className="mb-10 rounded-xl border border-gray-950/[0.06] bg-gray-950/[0.02] p-6"
          >
            <p className="text-[15px] text-gray-600 leading-relaxed">
              {content.extras}
            </p>
          </motion.div>
        )}

        {/* Worksheet sections */}
        {type === 'worksheet' && content.sections && (
          <motion.div variants={fadeIn} className="mb-10">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">
              Sections
            </h2>
            <div className="space-y-2">
              {content.sections.map((section, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 4 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.04, duration: 0.3 }}
                  className="flex items-start gap-4 p-4 rounded-lg border border-gray-950/[0.04] bg-gray-950/[0.015]"
                >
                  <span className="flex-shrink-0 w-6 h-6 rounded-full border border-gray-200 flex items-center justify-center text-xs font-medium text-gray-400 bg-white">
                    {i + 1}
                  </span>
                  <p className="text-[15px] text-gray-600 leading-relaxed pt-0.5">{section}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Placeholder */}
        <motion.div
          variants={fadeIn}
          className="mb-10 py-12 rounded-xl border border-dashed border-gray-950/[0.08] text-center"
        >
          <p className="text-sm text-gray-400">
            {type === 'live'
              ? 'Live session content and recording will appear here'
              : type === 'article'
                ? 'Full article content coming soon'
                : 'Interactive worksheet coming soon'}
          </p>
        </motion.div>

        {/* Navigation */}
        <motion.div
          variants={fadeIn}
          className="border-t border-gray-950/[0.06] mt-12 pt-6 flex items-center justify-between"
        >
          {prevItem ? (
            <Link
              to={`/content/${prevItem.id}`}
              className="group flex flex-col max-w-[45%]"
            >
              <span className="text-xs text-gray-400 mb-1">Previous</span>
              <span className="text-sm text-gray-500 group-hover:text-gray-900 transition-colors truncate">
                &larr; {findContentById(prevItem.id)?.content.shortTitle || findContentById(prevItem.id)?.content.title}
              </span>
            </Link>
          ) : (
            <div />
          )}
          {nextItem ? (
            <Link
              to={`/content/${nextItem.id}`}
              className="group flex flex-col text-right max-w-[45%]"
            >
              <span className="text-xs text-gray-400 mb-1">Next</span>
              <span className="text-sm text-gray-500 group-hover:text-gray-900 transition-colors truncate">
                {findContentById(nextItem.id)?.content.shortTitle || findContentById(nextItem.id)?.content.title} &rarr;
              </span>
            </Link>
          ) : (
            <div />
          )}
        </motion.div>
      </motion.article>
    </div>
  );
}
